import React, { useState, useEffect } from "react";
import { database } from "../firebaseConfig";
import { ref, update, onValue, push, remove } from "firebase/database";
import "./WorldSettingsTopics.css";
import downArrow from '../img/down.svg';

const WorldSettingsTopics = ({ userId }) => {
  const [availableTopics, setAvailableTopics] = useState({});
  const [selectedTopics, setSelectedTopics] = useState({});
  const [message, setMessage] = useState("");
  const [showAddForm, setShowAddForm] = useState(false);
  const [newTopic, setNewTopic] = useState({
    topic: "",
    topicSpecificNote: "",
    type: "intro",
    subjectLine: "",
    aiTagId: ""
  });
  const [customTopics, setCustomTopics] = useState({});
  const [isProcessing, setIsProcessing] = useState(false);
  const [editingTopicId, setEditingTopicId] = useState(null);
  const [openTopicId, setOpenTopicId] = useState(null);
  const [inlineEditId, setInlineEditId] = useState(null);
  const [inlineEditData, setInlineEditData] = useState({ subjectLine: '', topicSpecificNote: '', topic: '', aiTagId: '' });
  const [aiTags, setAiTags] = useState([]);
  const [infoCollapsed, setInfoCollapsed] = useState(true);

  // Vereinfache die verfügbaren Topic-Typen
  const topicTypes = [
    { value: 'intro', label: 'Introduction Email' },
    { value: 'follow_up_1', label: 'First Follow-Up' },
    { value: 'follow_up_2', label: 'Second Follow-Up' },
    { value: 'follow_up_3', label: 'Third Follow-Up' },
    { value: 'custom_reply', label: 'Custom Reply' }
  ];

  // Lade verfügbare Topics
  useEffect(() => {
    if (!userId) return;
    
    // Lade alle benötigten Daten in einer koordinierten Weise
    const loadAllTopicData = async () => {
      try {
        // Referenzen erstellen
        const availableTopicsRef = ref(database, '/WorldSettings/topicsTemplate');
        const selectedTopicsRef = ref(database, `/World/${userId}/settings/selectedTopics`);
        const customTopicsRef = ref(database, `/World/${userId}/settings/customTopics`);
        
        // Daten laden
        const availableSnapshot = await new Promise(resolve => {
          onValue(availableTopicsRef, snapshot => resolve(snapshot), { onlyOnce: true });
        });
        
        const selectedSnapshot = await new Promise(resolve => {
          onValue(selectedTopicsRef, snapshot => resolve(snapshot), { onlyOnce: true });
        });
        
        const customSnapshot = await new Promise(resolve => {
          onValue(customTopicsRef, snapshot => resolve(snapshot), { onlyOnce: true });
        });
        
        // States aktualisieren
        if (availableSnapshot.exists()) {
          setAvailableTopics(availableSnapshot.val());
        }
        
        if (selectedSnapshot.exists()) {
          setSelectedTopics(selectedSnapshot.val());
        } else {
          setSelectedTopics({});
        }
        
        if (customSnapshot.exists()) {
          setCustomTopics(customSnapshot.val());
        } else {
          setCustomTopics({});
        }
        
        console.log("Alle Topic-Daten erfolgreich geladen");
      } catch (error) {
        console.error("Fehler beim Laden der Topic-Daten:", error);
      }
    };
    
    // Initial alle Daten laden
    loadAllTopicData();
    
    // Dann Live-Updates für jede Datenquelle einrichten
    const availableTopicsRef = ref(database, '/WorldSettings/topicsTemplate');
    const selectedTopicsRef = ref(database, `/World/${userId}/settings/selectedTopics`);
    const customTopicsRef = ref(database, `/World/${userId}/settings/customTopics`);
    
    const unsubscribeAvailable = onValue(availableTopicsRef, (snapshot) => {
      if (snapshot.exists()) {
        setAvailableTopics(snapshot.val());
      }
    });
    
    const unsubscribeSelected = onValue(selectedTopicsRef, (snapshot) => {
      if (snapshot.exists()) {
        setSelectedTopics(snapshot.val());
      } else {
        setSelectedTopics({});
      }
    });
    
    const unsubscribeCustom = onValue(customTopicsRef, (snapshot) => {
      if (snapshot.exists()) {
        setCustomTopics(snapshot.val());
      } else {
        setCustomTopics({});
      }
    });
    
    return () => {
      unsubscribeAvailable();
      unsubscribeSelected();
      unsubscribeCustom();
    };
  }, [userId]);

  // Lade AI Tags aus der Datenbank
  useEffect(() => {
    if (!userId) return;
    
    const aiTagsRef = ref(database, `/World/${userId}/settings/aiTags`);
    const unsubscribe = onValue(aiTagsRef, (snapshot) => {
      if (snapshot.exists()) {
        const tagsData = [];
        snapshot.forEach((childSnapshot) => {
          tagsData.push({
            id: childSnapshot.key,
            ...childSnapshot.val()
          });
        });
        setAiTags(tagsData);
        console.log('🏷️ [TOPICS-SETTINGS] Loaded AI tags:', tagsData);
      } else {
        setAiTags([]);
      }
    });
    
    return () => unsubscribe();
  }, [userId]);

  // Aktualisiere die Funktion zum Gruppieren der Topics nach Typ
  const groupTopicsByType = () => {
    const groupedTopics = {
      intro: [],
      follow_up_1: [],
      follow_up_2: [],
      follow_up_3: [],
      custom_reply: []
    };

    // Füge nur benutzerdefinierte Topics hinzu
    Object.entries(customTopics).forEach(([id, topicData]) => {
      const type = topicData.type || 'intro';
      if (groupedTopics[type]) {
        groupedTopics[type].push({
          id,
          ...topicData,
          isCustom: true
        });
      }
    });

    return groupedTopics;
  };

  // Finde das ausgewählte Topic für einen bestimmten Typ
  const getSelectedTopicForType = (type) => {
    if (Object.keys(selectedTopics).length === 0) return null;
    
    if (type === 'custom_reply') {
      // Für custom_reply: Gib alle ausgewählten Topics zurück
      const customReplyTopics = Object.entries(selectedTopics)
        .filter(([_, data]) => data.type === 'custom_reply')
        .map(([id, _]) => id);
      
      return customReplyTopics.length > 0 ? customReplyTopics : null;
    } else {
      // Für andere Typen: Suche nach einem Topic mit dem angegebenen Typ
      const found = Object.entries(selectedTopics).find(([_, data]) => data.type === type);
      
      // Wenn gefunden, gib die ID zurück
      if (found) {
        return found[0];
      }
      
      return null;
    }
  };

  const handleTopicToggle = async (topicId, topicData) => {
    // Verhindere mehrfache schnelle Klicks
    if (isProcessing) return;
    setIsProcessing(true);
    
    try {
      const updatedTopics = { ...selectedTopics };
      
      // Unterschiedliche Logik für custom_reply und andere Topic-Typen
      if (topicData.type === 'custom_reply') {
        // Für custom_reply: Toggle-Verhalten (hinzufügen/entfernen)
        if (updatedTopics[topicId]) {
          // Wenn bereits ausgewählt, entferne es
          delete updatedTopics[topicId];
        } else {
          // Wenn nicht ausgewählt, füge es hinzu
          updatedTopics[topicId] = {
            topic: topicData.topic,
            topicSpecificNote: topicData.topicSpecificNote,
            type: topicData.type,
            ...(topicData.subjectLine && { subjectLine: topicData.subjectLine }),
            ...(topicData.isCustom && { isCustom: true }),
            aiTagId: topicData.aiTagId
          };
        }
      } else {
        // Für andere Topic-Typen: Nur eines auswählen
        const currentSelectedTopicId = getSelectedTopicForType(topicData.type);
        
        // Wenn das gleiche Topic bereits ausgewählt ist, entferne es
        if (currentSelectedTopicId === topicId) {
          delete updatedTopics[topicId];
        } else {
          // Wenn ein anderes Topic des gleichen Typs ausgewählt ist, entferne es
          if (currentSelectedTopicId) {
            delete updatedTopics[currentSelectedTopicId];
          }
          
          // Füge das neue Topic hinzu
          updatedTopics[topicId] = {
            topic: topicData.topic,
            topicSpecificNote: topicData.topicSpecificNote,
            type: topicData.type,
            ...(topicData.subjectLine && { subjectLine: topicData.subjectLine }),
            ...(topicData.isCustom && { isCustom: true }),
            aiTagId: topicData.aiTagId
          };
        }
      }
      
      // Optimistisches UI-Update
      setSelectedTopics(updatedTopics);
      
      // Batch-Update für die Datenbank
      const batch = {};
      
      // Speichere die ausgewählten Topics
      batch[`/World/${userId}/settings/selectedTopics`] = updatedTopics;
      
      // Erstelle Topic-Mapping
      const topicMapping = {};
      Object.entries(updatedTopics).forEach(([id, data]) => {
        // Für custom_reply: Speichere alle ausgewählten Topics in einem Array
        if (data.type === 'custom_reply') {
          if (!topicMapping[data.type]) {
            topicMapping[data.type] = [];
          }
          topicMapping[data.type].push(id);
        } else {
          // Für andere Typen: Speichere nur die ID
          topicMapping[data.type] = id;
        }
      });
      
      batch[`/World/${userId}/settings/topicMapping`] = topicMapping;
      
      // Führe alle Updates in einer Transaktion durch
      await update(ref(database), batch);
      
      setMessage(`Topic ${updatedTopics[topicId] ? 'selected' : 'deselected'} successfully.`);
      setTimeout(() => setMessage(""), 3000);
    } catch (error) {
      console.error("Error updating topics:", error);
      setMessage("Error updating topics. Please try again.");
      
      // Bei Fehler den State zurücksetzen
      const userTopicsRef = ref(database, `/World/${userId}/settings/selectedTopics`);
      onValue(userTopicsRef, (snapshot) => {
        if (snapshot.exists()) {
          setSelectedTopics(snapshot.val());
        } else {
          setSelectedTopics({});
        }
      }, { onlyOnce: true });
    } finally {
      setIsProcessing(false);
    }
  };

  // Starte die Bearbeitung eines benutzerdefinierten Topics
  const handleEditCustomTopic = (topicId) => {
    const topicData = customTopics[topicId];
    if (topicData) {
      setEditingTopicId(topicId);
      setNewTopic({
        topic: topicData.topic,
        topicSpecificNote: topicData.topicSpecificNote,
        type: topicData.type || 'intro',
        subjectLine: topicData.subjectLine || '',
        aiTagId: topicData.aiTagId || ''
      });
      setShowAddForm(true);
    }
  };

  // Aktualisiere die Funktion zum Hinzufügen oder Aktualisieren eines benutzerdefinierten Topics
  const handleSaveCustomTopic = async (e) => {
    e.preventDefault();
    
    if (!newTopic.topic || !newTopic.topicSpecificNote) {
      setMessage("Please fill in all required fields.");
      return;
    }

    try {
      setIsProcessing(true);
      const customTopicsRef = ref(database, `/World/${userId}/settings/customTopics`);
      
      // Stelle sicher, dass der Topic-Typ einer der erlaubten Typen ist
      if (!['intro', 'follow_up_1', 'follow_up_2', 'follow_up_3', 'custom_reply'].includes(newTopic.type)) {
        newTopic.type = 'custom_reply'; // Standardmäßig auf custom_reply setzen
      }
      
      // Bestimme, ob ein bestehendes Topic bearbeitet oder ein neues erstellt wird
      const topicId = editingTopicId || `custom_${Date.now()}`;
      
      await update(customTopicsRef, {
        [topicId]: {
          topic: newTopic.topic,
          topicSpecificNote: newTopic.topicSpecificNote,
          type: newTopic.type,
          subjectLine: newTopic.subjectLine || "",
          aiTagId: newTopic.aiTagId || ""
        }
      });
      
      // Aktualisiere auch die ausgewählten Topics, falls das bearbeitete Topic ausgewählt war
      if (selectedTopics[topicId]) {
        const selectedTopicsRef = ref(database, `/World/${userId}/settings/selectedTopics/${topicId}`);
        await update(selectedTopicsRef, {
          topic: newTopic.topic,
          topicSpecificNote: newTopic.topicSpecificNote,
          type: newTopic.type,
          subjectLine: newTopic.subjectLine || "",
          isCustom: true,
          aiTagId: newTopic.aiTagId || ""
        });
      }
      
      // Zurücksetzen der Formularfelder
      setNewTopic({
        topic: "",
        topicSpecificNote: "",
        type: "intro",
        subjectLine: "",
        aiTagId: ""
      });
      
      setEditingTopicId(null);
      setShowAddForm(false);
      setMessage(editingTopicId ? "Topic successfully updated." : "Custom topic successfully added.");
      setTimeout(() => setMessage(""), 3000);
    } catch (error) {
      console.error("Error saving custom topic", error);
      setMessage("Error saving custom topic.");
    } finally {
      setIsProcessing(false);
    }
  };

  const handleDeleteCustomTopic = async (topicId) => {
    try {
      // Entferne das Topic aus den benutzerdefinierten Topics
      const customTopicsRef = ref(database, `/World/${userId}/settings/customTopics/${topicId}`);
      await remove(customTopicsRef);
      
      // Wenn es auch in den ausgewählten Topics ist, entferne es dort auch
      if (selectedTopics[topicId]) {
        const updatedSelectedTopics = { ...selectedTopics };
        delete updatedSelectedTopics[topicId];
        
        const selectedTopicsRef = ref(database, `/World/${userId}/settings/selectedTopics`);
        await update(selectedTopicsRef, updatedSelectedTopics);
        
        // Aktualisiere den lokalen State
        setSelectedTopics(updatedSelectedTopics);
        
        // Aktualisiere das Topic-Mapping
        const topicMappingRef = ref(database, `/World/${userId}/settings/topicMapping`);
        const topicMapping = {};
        
        // Erstelle eine Zuordnung von Typ zu ID
        Object.entries(updatedSelectedTopics).forEach(([id, data]) => {
          topicMapping[data.type] = id;
        });
        
        await update(topicMappingRef, topicMapping);
      }
      
      // Aktualisiere den lokalen State für benutzerdefinierte Topics
      const updatedCustomTopics = { ...customTopics };
      delete updatedCustomTopics[topicId];
      setCustomTopics(updatedCustomTopics);
      
      setMessage("Custom topic successfully deleted.");
      setTimeout(() => setMessage(""), 3000);
    } catch (error) {
      console.error("Error deleting custom topic", error);
      setMessage("Error deleting custom topic.");
    }
  };

  // Funktion zum Abbrechen der Bearbeitung
  const handleCancelEdit = () => {
    setEditingTopicId(null);
    setNewTopic({
      topic: "",
      topicSpecificNote: "",
      type: "intro",
      subjectLine: "",
      aiTagId: ""
    });
    setShowAddForm(false);
  };

  if (!userId) {
    return <p className="errorMessage">Please login to save settings.</p>;
  }

  const groupedTopics = groupTopicsByType();

  return (
    <>
      {/* Info Container über Topics */}
      <div className={`topicsInfoContainer ${infoCollapsed ? 'collapsed' : ''}`}>
        <div className="topicsInfoHeader" onClick={() => setInfoCollapsed(!infoCollapsed)}>
          <div className="topicsInfoIcon">
            <img src="/img/info.svg" alt="Info" style={{ width: '34px', height: '34px' }} />
          </div>
          <h3>What are Email Topics?</h3>
          <button className="topicsInfoToggle">
            <img 
              src="/img/down.svg" 
              alt="Toggle" 
              className={`toggleIcon ${infoCollapsed ? 'collapsed' : ''}`}
              style={{ width: '16px', height: '16px' }}
            />
          </button>
        </div>
        {!infoCollapsed && (
          <div className="topicsInfoContent">
            <p>
              Email Topics are predefined message templates that help structure your outreach campaigns. 
              Each topic contains specific instructions for different types of email sequences and responses.
            </p>
            <div className="topicsInfoFeatures">
              <div className="topicsInfoFeature">
                <span className="featureIcon">📧</span>
                <span>Structured email sequences</span>
              </div>
              <div className="topicsInfoFeature">
                <span className="featureIcon">🎯</span>
                <span>Targeted messaging templates</span>
              </div>
              <div className="topicsInfoFeature">
                <span className="featureIcon">📝</span>
                <span>Custom reply frameworks</span>
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="topics-container">
        <h3>Email Topics Selection</h3>
        
        <div className="topics-button-container">
          <button 
            className="topics-add-button"
            onClick={() => {
              if (showAddForm && editingTopicId) {
                handleCancelEdit();
              } else {
                setShowAddForm(!showAddForm);
              }
            }}
          >
            {showAddForm ? "Cancel" : "Add Custom Topic"}
          </button>
        </div>
        
        {showAddForm && (
          <div className="topics-form">
            <h4>{editingTopicId ? 'Edit Topic' : 'Create New Topic'}</h4>
            <form onSubmit={handleSaveCustomTopic}>
              <div className="topics-form-group">
                <label>Topic Type:</label>
                <select
                  value={newTopic.type}
                  onChange={(e) => setNewTopic({...newTopic, type: e.target.value})}
                  className="topics-type-select"
                  disabled={isProcessing}
                >
                  {topicTypes.map(type => (
                    <option key={type.value} value={type.value}>
                      {type.label}
                    </option>
                  ))}
                </select>
              </div>
              
              <div className="topics-form-group">
                <label>Topic Name:</label>
                <input
                  type="text"
                  value={newTopic.topic}
                  onChange={(e) => setNewTopic({...newTopic, topic: e.target.value})}
                  placeholder="e.g. My Custom Topic"
                  required
                />
              </div>
              
              <div className="topics-form-group">
                <label>Subject Line (optional):</label>
                <input
                  type="text"
                  value={newTopic.subjectLine}
                  onChange={(e) => setNewTopic({...newTopic, subjectLine: e.target.value})}
                  placeholder="e.g. BonusClub - Boost Your Online Visibility"
                />
              </div>
              
              <div className="topics-form-group">
                <label>Topic-specific Instructions:</label>
                <textarea
                  value={newTopic.topicSpecificNote}
                  onChange={(e) => setNewTopic({...newTopic, topicSpecificNote: e.target.value})}
                  placeholder="Instructions for the AI on how to handle this topic..."
                  rows={5}
                  required
                />
              </div>
              
              <div className="topics-form-group">
                <label>AI Tag:</label>
                <select
                  value={newTopic.aiTagId}
                  onChange={(e) => setNewTopic({...newTopic, aiTagId: e.target.value})}
                  className="topics-ai-tag-select"
                  disabled={isProcessing}
                >
                  <option value="">Select an AI Tag</option>
                  {aiTags.map(tag => (
                    <option key={tag.id} value={tag.id}>{tag.name}</option>
                  ))}
                </select>
              </div>
              
              <div className="topics-form-actions">
                  <button type="submit" className="topics-save-button">
                  {editingTopicId ? 'Update Topic' : 'Add Topic'}
                </button>
                <button 
                  type="button" 
                  className="topics-cancel-button"
                  onClick={handleCancelEdit}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        )}
        
        {Object.entries(groupedTopics).map(([type, topics]) => {
          const selectedTopicId = getSelectedTopicForType(type);
          const isCustomReplyType = type === 'custom_reply';
          
          // Bestimme die Anzahl der ausgewählten Topics
          let selectedCount = 0;
          if (isCustomReplyType && selectedTopicId) {
            selectedCount = selectedTopicId.length;
          } else if (selectedTopicId) {
            selectedCount = 1;
          }
          
          return (
            <div key={type} className="topics-category">
              <h4 className="topics-category-title">
                {topicTypes.find(t => t.value === type)?.label || type}
                {selectedCount > 0 ? 
                  <span className="topics-selected-indicator"> ({selectedCount} selected)</span> : 
                  <span className="topics-not-selected-indicator"> (none selected)</span>
                }
              </h4>
              <div className="topics-list">
                {topics.map(topicData => {
                  let isSelected = false;
                  if (isCustomReplyType && selectedTopicId) {
                    isSelected = selectedTopicId.includes(topicData.id);
                  } else {
                    isSelected = selectedTopicId === topicData.id;
                  }
                  const isOpen = openTopicId === topicData.id;
                  const isEditing = inlineEditId === topicData.id;
                  return (
                    <div key={topicData.id} className={`topics-item ${isSelected ? 'selected' : ''} ${topicData.isCustom ? 'custom' : ''}`}>
                      <div
                        className="topics-header"
                        onClick={() => setOpenTopicId(isOpen ? null : topicData.id)}
                        style={{ cursor: 'pointer' }}
                      >
                        <label className="topics-checkbox" onClick={e => e.stopPropagation()}>
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => handleTopicToggle(topicData.id, topicData)}
                          />
                          <span className="topics-name">
                            {topicData.topic}
                            {topicData.isCustom && (
                              <span className="topics-custom-badge">Custom</span>
                            )}
                          </span>
                        </label>
                        <div className="topics-custom-actions">
                          <button
                            className={`topics-edit-button ${inlineEditId === topicData.id ? 'topics-edit-button-cancel' : ''}`}
                            onClick={e => {
                              e.preventDefault();
                              e.stopPropagation();
                              // Toggle Edit Mode
                              if (inlineEditId === topicData.id) {
                                // Cancel Edit Mode
                                setInlineEditId(null);
                                setInlineEditData({ subjectLine: '', topicSpecificNote: '', topic: '', aiTagId: '' });
                              } else {
                                // Start Edit Mode
                                setInlineEditId(topicData.id);
                                setInlineEditData({
                                  subjectLine: topicData.subjectLine || '',
                                  topicSpecificNote: topicData.topicSpecificNote || '',
                                  topic: topicData.topic || '',
                                  aiTagId: topicData.aiTagId || ''
                                });
                                setOpenTopicId(topicData.id);
                              }
                            }}
                            title={inlineEditId === topicData.id ? "Cancel editing" : "Edit template"}
                          >
                            {inlineEditId === topicData.id ? "Cancel" : "Edit"}
                          </button>
                          {topicData.isCustom && (
                            <button
                              className="topics-delete-button"
                              onClick={e => {
                                e.preventDefault();
                                e.stopPropagation();
                                // Bestätigung vor dem Löschen
                                const confirmDelete = window.confirm(
                                  `Are you sure you want to delete the topic "${topicData.topic}"? This action cannot be undone.`
                                );
                                if (confirmDelete) {
                                  handleDeleteCustomTopic(topicData.id);
                                }
                              }}
                              title="Delete topic"
                            >
                              ×
                            </button>
                          )}
                        </div>
                        <span
                          style={{
                            marginLeft: 8,
                            width: 18,
                            height: 18,
                            display: 'inline-block',
                            transition: 'transform 0.2s',
                            transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)'
                          }}
                        >
                          <img src={downArrow} alt="arrow" style={{ width: 18, height: 18, display: 'block' }} />
                        </span>
                      </div>
                      {isOpen && (
                        <div className="topics-body">
                          {isEditing ? (
                            <form
                              className="topics-inline-edit-form"
                              onSubmit={async e => {
                                e.preventDefault();
                                setIsProcessing(true);
                                // Update in DB
                                const updateRef = topicData.isCustom
                                  ? ref(database, `/World/${userId}/settings/customTopics/${topicData.id}`)
                                  : ref(database, `/WorldSettings/topicsTemplate/${topicData.id}`);
                                await update(updateRef, {
                                  subjectLine: inlineEditData.subjectLine,
                                  topicSpecificNote: inlineEditData.topicSpecificNote,
                                  topic: inlineEditData.topic,
                                  aiTagId: inlineEditData.aiTagId
                                });
                                setInlineEditId(null);
                                setIsProcessing(false);
                              }}
                            >
                              <div>
                                <label>Template Name</label>
                                <input
                                  type="text"
                                  value={inlineEditData.topic}
                                  onChange={e => setInlineEditData({ ...inlineEditData, topic: e.target.value })}
                                  placeholder="e.g. Welcome Email"
                                  required
                                />
                              </div>
                              <div>
                                <label>Subject Line</label>
                                <input
                                  type="text"
                                  value={inlineEditData.subjectLine}
                                  onChange={e => setInlineEditData({ ...inlineEditData, subjectLine: e.target.value })}
                                  placeholder="e.g. BonusClub - Boost Your Online Visibility"
                                />
                              </div>
                              <div>
                                <label>Topic-specific Instructions</label>
                                <textarea
                                  value={inlineEditData.topicSpecificNote}
                                  onChange={e => setInlineEditData({ ...inlineEditData, topicSpecificNote: e.target.value })}
                                  rows={5}
                                  required
                                />
                              </div>
                              <div className="topics-inline-edit-group">
                                <label>AI Tag:</label>
                                <select
                                  value={inlineEditData.aiTagId}
                                  onChange={e => setInlineEditData({ ...inlineEditData, aiTagId: e.target.value })}
                                  className="topics-ai-tag-select"
                                  disabled={isProcessing}
                                >
                                  <option value="">Select an AI Tag</option>
                                  {aiTags.map(tag => (
                                    <option key={tag.id} value={tag.id}>{tag.name}</option>
                                  ))}
                                </select>
                              </div>
                              <div className="topics-inline-edit-actions">
                                <button type="submit" className="topics-inline-edit-save" disabled={isProcessing}>Save</button>
                                <button
                                  type="button"
                                  className="topics-inline-edit-cancel"
                                  onClick={() => setInlineEditId(null)}
                                  disabled={isProcessing}
                                >Cancel</button>
                              </div>
                            </form>
                          ) : (
                            <>
                              {topicData.subjectLine && (
                                <p className="topics-subject"><strong>Subject:</strong> {topicData.subjectLine}</p>
                              )}
                              <p className="topics-note">{topicData.topicSpecificNote}</p>
                              {topicData.aiTagId && (
                                <div className="topics-ai-tag-info">
                                  <strong>AI Tag:</strong> 
                                  {(() => {
                                    const associatedTag = aiTags.find(tag => tag.id === topicData.aiTagId);
                                    return associatedTag ? (
                                      <span className="topics-ai-tag-badge" style={{ backgroundColor: associatedTag.color }}>
                                        {associatedTag.icon && (
                                          <img src={associatedTag.icon} alt="" className="topics-ai-tag-icon" />
                                        )}
                                        {associatedTag.name}
                                      </span>
                                    ) : (
                                      <span className="topics-ai-tag-missing">Tag not found</span>
                                    );
                                  })()}
                                </div>
                              )}
                            </>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
        
        {message && <p className="topics-message">{message}</p>}
      </div>
    </>
  );
};

export default WorldSettingsTopics; 